if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.zoom = {
          image: null,
          scale: 1,
          translateX: 0,
          translateY: 0,
          pointers: new Map(),
          pointerStart: new Map(),
          didPinch: false,
          lastTapTime: 0,
          lastTapX: 0,
          lastTapY: 0,
          maxScale: 5,
        };

        this.onZoomPointerDown = this.onZoomPointerDown.bind(this);
        this.onZoomPointerMove = this.onZoomPointerMove.bind(this);
        this.onZoomPointerUp = this.onZoomPointerUp.bind(this);
        this.preventNativeImageGesture = this.preventNativeImageGesture.bind(this);
      }

      hide() {
        this.disableZoomForCurrentMedia();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();

        this.enableZoomForActiveMedia(activeMedia);
      }

      enableZoomForActiveMedia(activeMedia) {
        this.disableZoomForCurrentMedia();
        if (!activeMedia || activeMedia.nodeName !== 'IMG') return;

        this.zoom.image = activeMedia;
        this.zoom.scale = 1;
        this.zoom.translateX = 0;
        this.zoom.translateY = 0;
        this.zoom.pointers.clear();
        this.zoom.pointerStart.clear();
        this.zoom.didPinch = false;
        this.zoom.lastTapTime = 0;

        activeMedia.addEventListener('pointerdown', this.onZoomPointerDown);
        activeMedia.addEventListener('pointermove', this.onZoomPointerMove);
        activeMedia.addEventListener('pointerup', this.onZoomPointerUp);
        activeMedia.addEventListener('pointercancel', this.onZoomPointerUp);
        activeMedia.addEventListener('touchstart', this.preventNativeImageGesture, { passive: false });
        activeMedia.addEventListener('touchmove', this.preventNativeImageGesture, { passive: false });
        activeMedia.addEventListener('touchend', this.preventNativeImageGesture, { passive: false });
        activeMedia.classList.add('product-media-modal__image--zoomable');
        this.applyZoomTransform();
      }

      disableZoomForCurrentMedia() {
        if (!this.zoom.image) return;

        this.zoom.image.removeEventListener('pointerdown', this.onZoomPointerDown);
        this.zoom.image.removeEventListener('pointermove', this.onZoomPointerMove);
        this.zoom.image.removeEventListener('pointerup', this.onZoomPointerUp);
        this.zoom.image.removeEventListener('pointercancel', this.onZoomPointerUp);
        this.zoom.image.removeEventListener('touchstart', this.preventNativeImageGesture);
        this.zoom.image.removeEventListener('touchmove', this.preventNativeImageGesture);
        this.zoom.image.removeEventListener('touchend', this.preventNativeImageGesture);
        this.zoom.image.classList.remove(
          'product-media-modal__image--zoomable',
          'product-media-modal__image--zoomed',
          'product-media-modal__image--dragging'
        );
        this.zoom.image.style.removeProperty('transform');
        this.zoom.image = null;
        this.zoom.pointers.clear();
        this.zoom.pointerStart.clear();
      }

      resetZoom() {
        this.zoom.scale = 1;
        this.zoom.translateX = 0;
        this.zoom.translateY = 0;
        this.zoom.pointers.clear();
        this.zoom.pointerStart.clear();
        this.zoom.didPinch = false;
        this.applyZoomTransform();
      }

      preventNativeImageGesture(event) {
        if (event.cancelable) event.preventDefault();
      }

      onZoomPointerDown(event) {
        if (event.pointerType !== 'touch') return;

        event.preventDefault();
        if (this.zoom.image.setPointerCapture) this.zoom.image.setPointerCapture(event.pointerId);
        const point = this.getPointerPosition(event);

        if (this.zoom.pointers.size === 0) this.zoom.didPinch = false;

        this.zoom.pointers.set(event.pointerId, point);
        this.zoom.pointerStart.set(event.pointerId, { ...point, time: Date.now() });
        this.zoom.image.classList.add('product-media-modal__image--dragging');

        if (this.zoom.pointers.size === 2) {
          this.zoom.didPinch = true;
        }
      }

      onZoomPointerMove(event) {
        if (!this.zoom.pointers.has(event.pointerId)) return;

        event.preventDefault();
        const previousPoints = this.getZoomPoints();
        const previousPoint = this.zoom.pointers.get(event.pointerId);
        const nextPoint = this.getPointerPosition(event);
        this.zoom.pointers.set(event.pointerId, nextPoint);

        if (this.zoom.pointers.size === 1 && this.zoom.scale > 1) {
          this.zoom.translateX += nextPoint.x - previousPoint.x;
          this.zoom.translateY += nextPoint.y - previousPoint.y;
          this.clampZoomTranslate();
          this.applyZoomTransform();
          return;
        }

        if (this.zoom.pointers.size === 2) {
          const points = this.getZoomPoints();
          const previousDistance = this.getDistance(previousPoints[0], previousPoints[1]);
          const nextDistance = this.getDistance(points[0], points[1]);
          if (previousDistance === 0) return;

          const previousMidpoint = this.getMidpoint(previousPoints[0], previousPoints[1]);
          const nextMidpoint = this.getMidpoint(points[0], points[1]);
          const previousScale = this.zoom.scale;
          const nextScale = this.clamp(previousScale * (nextDistance / previousDistance), 1, this.zoom.maxScale);

          this.zoom.translateX += nextMidpoint.x - previousMidpoint.x;
          this.zoom.translateY += nextMidpoint.y - previousMidpoint.y;
          this.zoomToPoint(nextScale, nextMidpoint, previousScale);
        }
      }

      onZoomPointerUp(event) {
        if (!this.zoom.pointers.has(event.pointerId)) return;

        event.preventDefault();
        const point = this.getPointerPosition(event);
        const pointerStart = this.zoom.pointerStart.get(event.pointerId);
        const wasSingleTap = this.zoom.pointers.size === 1;
        const hadPinched = this.zoom.didPinch;
        this.zoom.pointers.delete(event.pointerId);
        this.zoom.pointerStart.delete(event.pointerId);

        if (this.zoom.pointers.size === 0) {
          this.zoom.image.classList.remove('product-media-modal__image--dragging');
          this.zoom.didPinch = false;
        }

        if (wasSingleTap && !hadPinched && pointerStart && this.isTapGesture(pointerStart, point)) this.handleDoubleTap(point);
      }

      handleDoubleTap(point) {
        const now = Date.now();
        const tapDistance = Math.hypot(point.x - this.zoom.lastTapX, point.y - this.zoom.lastTapY);
        const isDoubleTap = now - this.zoom.lastTapTime < 300 && tapDistance < 40;

        this.zoom.lastTapTime = now;
        this.zoom.lastTapX = point.x;
        this.zoom.lastTapY = point.y;

        if (!isDoubleTap) return;

        if (this.zoom.scale > 1) {
          this.resetZoom();
          return;
        }

        this.zoomToPoint(this.getActualSizeScale(), point, this.zoom.scale);
      }

      zoomToPoint(nextScale, point, previousScale = this.zoom.scale, shouldSetScale = true) {
        if (!this.zoom.image) return;

        const rect = this.zoom.image.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2 - this.zoom.translateX;
        const centerY = rect.top + rect.height / 2 - this.zoom.translateY;
        const ratio = nextScale / previousScale;

        this.zoom.translateX = point.x - centerX - (point.x - centerX - this.zoom.translateX) * ratio;
        this.zoom.translateY = point.y - centerY - (point.y - centerY - this.zoom.translateY) * ratio;
        if (shouldSetScale) this.zoom.scale = nextScale;
        this.clampZoomTranslate();
        this.applyZoomTransform();
      }

      applyZoomTransform() {
        if (!this.zoom.image) return;

        this.zoom.image.style.transform = `translate3d(${this.zoom.translateX}px, ${this.zoom.translateY}px, 0) scale(${this.zoom.scale})`;
        this.zoom.image.classList.toggle('product-media-modal__image--zoomed', this.zoom.scale > 1);
      }

      clampZoomTranslate() {
        if (!this.zoom.image) return;

        if (this.zoom.scale <= 1) {
          this.zoom.translateX = 0;
          this.zoom.translateY = 0;
          return;
        }

        const container = this.querySelector('[role="document"]');
        const containerRect = container.getBoundingClientRect();
        const imageWidth = this.zoom.image.offsetWidth * this.zoom.scale;
        const imageHeight = this.zoom.image.offsetHeight * this.zoom.scale;
        const maxX = Math.max(0, (imageWidth - containerRect.width) / 2);
        const maxY = Math.max(0, (imageHeight - containerRect.height) / 2);

        this.zoom.translateX = this.clamp(this.zoom.translateX, -maxX, maxX);
        this.zoom.translateY = this.clamp(this.zoom.translateY, -maxY, maxY);
      }

      getActualSizeScale() {
        if (!this.zoom.image) return 2;

        const rect = this.zoom.image.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return 2;

        const naturalWidth = this.zoom.image.naturalWidth || Number(this.zoom.image.getAttribute('width')) || rect.width;
        const naturalHeight = this.zoom.image.naturalHeight || Number(this.zoom.image.getAttribute('height')) || rect.height;
        const widthScale = naturalWidth / rect.width;
        const heightScale = naturalHeight / rect.height;

        return this.clamp(Math.max(widthScale, heightScale), 2, this.zoom.maxScale);
      }

      getPointerPosition(event) {
        return {
          x: event.clientX,
          y: event.clientY,
        };
      }

      getZoomPoints() {
        return Array.from(this.zoom.pointers.values());
      }

      getDistance(pointA, pointB) {
        return Math.hypot(pointA.x - pointB.x, pointA.y - pointB.y);
      }

      getMidpoint(pointA, pointB) {
        return {
          x: (pointA.x + pointB.x) / 2,
          y: (pointA.y + pointB.y) / 2,
        };
      }

      isTapGesture(startPoint, endPoint) {
        return !this.zoom.didPinch && Date.now() - startPoint.time < 300 && Math.hypot(startPoint.x - endPoint.x, startPoint.y - endPoint.y) < 10;
      }

      clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
      }
    }
  );
}
